import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Processing type determines the messages shown.
 * - 'pdf': PDF generation
 * - 'patient_pdf': Patient evidence PDF
 * - 'email': Sending email
 * - 'table': Table generation
 * - 'pptx': PowerPoint presentation generation
 * - 'files': Uploaded file/document analysis
 * - 'general': Generic processing
 */
export type ProcessingType = 'pdf' | 'pptx' | 'patient_pdf' | 'email' | 'table' | 'files' | 'general';

interface ProcessingBlockProps {
  type?: ProcessingType;
  className?: string;
}

/** Processing message configuration for each type */
interface ProcessingConfig {
  title: string;
  messages: string[];
}

const TRANSLATION_KEYS: Record<ProcessingType, { titleKey: TranslationKey; messageKeys: TranslationKey[] }> = {
  pdf: {
    titleKey: 'chat.processingBlock.pdf.title',
    messageKeys: [
      'chat.processingBlock.pdf.preparingDocument',
      'chat.processingBlock.pdf.formattingContent',
      'chat.processingBlock.pdf.buildingPdf',
      'chat.processingBlock.pdf.almostReady',
    ],
  },
  pptx: {
    titleKey: 'chat.processingBlock.pptx.title',
    messageKeys: [
      'chat.processingBlock.pptx.preparingPresentation',
      'chat.processingBlock.pptx.creatingSlides',
      'chat.processingBlock.pptx.buildingPowerpoint',
      'chat.processingBlock.pptx.almostReady',
    ],
  },
  patient_pdf: {
    titleKey: 'chat.processingBlock.patientPdf.title',
    messageKeys: [
      'chat.processingBlock.patientPdf.compilingPatientInfo',
      'chat.processingBlock.patientPdf.preparingHandout',
      'chat.processingBlock.patientPdf.buildingDocument',
      'chat.processingBlock.patientPdf.finalizingContent',
    ],
  },
  email: {
    titleKey: 'chat.processingBlock.email.title',
    messageKeys: [
      'chat.processingBlock.email.preparingEmail',
      'chat.processingBlock.email.settingUpDelivery',
      'chat.processingBlock.email.almostThere',
      'chat.processingBlock.email.sendingShortly',
    ],
  },
  table: {
    titleKey: 'chat.processingBlock.table.title',
    messageKeys: [
      'chat.processingBlock.table.structuringData',
      'chat.processingBlock.table.buildingTable',
      'chat.processingBlock.table.formattingRows',
      'chat.processingBlock.table.almostReady',
    ],
  },
  files: {
    titleKey: 'chat.processingBlock.files.title',
    messageKeys: [
      'chat.processingBlock.files.processingRequest',
      'chat.processingBlock.files.extractingContent',
      'chat.processingBlock.files.analyzingDocuments',
      'chat.processingBlock.files.almostReady',
    ],
  },
  general: {
    titleKey: 'chat.processingBlock.general.title',
    messageKeys: [
      'chat.processingBlock.general.processingRequest',
      'chat.processingBlock.general.workingOnIt',
      'chat.processingBlock.general.almostThere',
      'chat.processingBlock.general.finishingUp',
    ],
  },
};

function useProcessingConfig(type: ProcessingType): ProcessingConfig {
  const { t } = useTranslations();
  const keys = TRANSLATION_KEYS[type];

  return useMemo(
    () => ({
      title: t(keys.titleKey),
      messages: keys.messageKeys.map((key) => t(key)),
    }),
    [t, keys]
  );
}

/** Delay before showing the entire block based on processing type (ms) */
const BLOCK_SHOW_DELAY_MS: Record<ProcessingType, number> = {
  pdf: 2000,
  pptx: 2000,
  patient_pdf: 2000,
  email: 1500,
  table: 2500,
  files: 1500, // Shorter delay — user just saw "Action completed" and needs prompt feedback
  general: 4000, // Longer delay for general - often resolves before needing to show
};

/** How long each message displays before transitioning (ms) */
const MESSAGE_INTERVAL_MS = 2500;

/** Fade transition duration (ms) */
const FADE_DURATION_MS = 300;

/**
 * ProcessingBlock - Shows contextual loading state after thinking completes
 *
 * Displays when:
 * - Thinking has completed but tool output hasn't started yet
 * - There's a delay between response phases
 *
 * Visual style matches ArtefactBlock's generating state for a seamless
 * transition when the actual tool call data arrives.
 *
 * Features:
 * - Customized messages per processing type (PDF, email, table, etc.)
 * - Smooth message transitions with fade effects
 * - Delayed appearance to prevent flicker on quick operations
 */
export const ProcessingBlock = ({
  type = 'general',
  className,
}: ProcessingBlockProps): JSX.Element | null => {
  const config = useProcessingConfig(type);
  const [showBlock, setShowBlock] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const isMountedRef = useRef(true);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup function for fade timeout
  const clearFadeTimeout = useCallback(() => {
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
  }, []);

  // Delay showing the entire block - prevents UI noise for quick operations
  useEffect(() => {
    isMountedRef.current = true;

    const delay = BLOCK_SHOW_DELAY_MS[type];
    const showTimer = setTimeout(() => {
      if (isMountedRef.current) {
        setShowBlock(true);
      }
    }, delay);

    return () => {
      isMountedRef.current = false;
      clearTimeout(showTimer);
      clearFadeTimeout();
    };
  }, [clearFadeTimeout, type]);

  // Cycle through messages with fade transitions
  useEffect(() => {
    if (!showBlock) return;

    const interval = setInterval(() => {
      if (!isMountedRef.current) return;

      // Start fade out
      setIsFading(true);
      clearFadeTimeout();

      // After fade out, change message and fade in
      fadeTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setMessageIndex((prev) => (prev + 1) % config.messages.length);
          setIsFading(false);
        }
      }, FADE_DURATION_MS);
    }, MESSAGE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      clearFadeTimeout();
    };
  }, [showBlock, clearFadeTimeout, config.messages.length]);

  // Don't render anything until delay passes
  if (!showBlock) {
    return null;
  }

  const currentMessage = config.messages[messageIndex] ?? config.messages[0]!;

  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4',
        'transition-all duration-200',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TypographyP2 className="font-medium text-text-primary">
          {config.title}
        </TypographyP2>
        <div className="flex items-center gap-1.5">
          <Loader2 className="size-3 animate-spin text-text-tertiary" />
          <TypographyCaption
            className={cn(
              'text-text-tertiary transition-opacity',
              isFading ? 'opacity-0' : 'opacity-100'
            )}
            style={{ transitionDuration: `${FADE_DURATION_MS}ms` }}
          >
            {currentMessage}...
          </TypographyCaption>
        </div>
      </div>
    </div>
  );
};
